/**
 * Logo ve favicon yükleme kuralları.
 *
 * Bu değerler backend'deki `FileStorageService` ile BİREBİR aynı olmak zorunda:
 * VALID_DIMENSIONS, VALID_MIME_TYPES ve MAX_SIZE. Sunucu zaten doğruluyor —
 * buradaki kopya iki iş için var:
 *
 *   1. Kullanıcıya hangi ölçülerin kabul edildiğini SÖYLEMEK. Önceden ekranda
 *      yalnız "PNG, JPG, JPEG (Maks 5MB)" yazıyordu; ölçü kısıtı hiçbir yerde
 *      görünmediği için kullanıcı neyi yanlış yaptığını anlayamıyordu.
 *   2. Dosya seçilir seçilmez tarayıcıda kontrol edip gereksiz yüklemeyi
 *      engellemek.
 *
 * Sunucudaki liste değişirse burası da güncellenmeli. Tek kaynak hâline
 * getirmek için ileride kuralları döndüren bir uç eklenebilir; bugün böyle
 * bir uç yok ve bu bilet kapsamında eklenmiyor.
 */

/** Sunucunun kabul ettiği tam ölçüler. Tolerans yoktur: 1200×401 reddedilir. */
export const BRANDING_DIMENSIONS = {
    logo: [
        { width: 1200, height: 400 },
        { width: 1500, height: 400 },
        { width: 1800, height: 500 },
        { width: 1920, height: 1080 },
        { width: 1000, height: 300 },
        { width: 1600, height: 400 },
    ],
    favicon: [
        { width: 32, height: 32 },
        { width: 64, height: 64 },
        { width: 500, height: 500 },
        { width: 1080, height: 1080 },
    ],
};

export const BRANDING_ACCEPT_ATTR = "image/png,image/jpeg,image/jpg";

export const BRANDING_MAX_BYTES = 5 * 1024 * 1024;

export const BRANDING_LABELS = { logo: "Logo", favicon: "Favicon" };

/** "1200×400, 1500×400, …" — ekranda ve hata metninde aynı biçim kullanılsın. */
export const formatAllowedDimensions = (type) =>
    (BRANDING_DIMENSIONS[type] || []).map((d) => `${d.width}×${d.height}`).join(", ");

/**
 * Seçilen görselin piksel ölçüsünü okur.
 *
 * Dosya okunamıyorsa (bozuk içerik, desteklenmeyen kodlama) null döner —
 * çağıran bu durumda kendi kontrolünü atlayıp sunucuya bırakır, çünkü asıl
 * doğrulama orada ve bozuk dosyayı `ImageIO` zaten reddediyor.
 */
export const readImageDimensions = (file) =>
    new Promise((resolve) => {
        const objectUrl = URL.createObjectURL(file);
        const img = new Image();

        img.onload = () => {
            const size = { width: img.naturalWidth, height: img.naturalHeight };
            URL.revokeObjectURL(objectUrl);
            resolve(size);
        };
        img.onerror = () => {
            URL.revokeObjectURL(objectUrl);
            resolve(null);
        };

        img.src = objectUrl;
    });

/**
 * Dosyayı sunucuya göndermeden önce kontrol eder.
 *
 * @returns {Promise<string|null>} Kullanıcıya gösterilecek hata metni; sorun
 *          yoksa null. Metinler sunucunun ürettiğiyle aynı bilgiyi taşır ki
 *          kullanıcı iki ayrı üslupla karşılaşmasın.
 */
export const validateBrandingFile = async (file, type) => {
    const label = BRANDING_LABELS[type] || "Görsel";

    if (!file) return null;

    const mime = (file.type || "").toLowerCase();
    if (!["image/png", "image/jpeg", "image/jpg"].includes(mime)) {
        return `${label} için yalnız PNG ve JPEG desteklenir.`;
    }

    if (file.size > BRANDING_MAX_BYTES) {
        return `${label} dosyası 5 MB sınırını aşıyor.`;
    }

    const allowed = BRANDING_DIMENSIONS[type];
    if (!allowed || allowed.length === 0) return null;

    const size = await readImageDimensions(file);
    // Ölçü okunamadıysa karar sunucunun: burada engellemek, geçerli bir dosyayı
    // tarayıcı kaynaklı bir sebeple reddetmek olurdu.
    if (!size) return null;

    const isAllowed = allowed.some(
        (d) => d.width === size.width && d.height === size.height
    );
    if (!isAllowed) {
        return `${label} için geçerli ölçüler: ${formatAllowedDimensions(type)}. `
            + `Seçilen dosya ${size.width}×${size.height}.`;
    }

    return null;
};
